// ============================================================
// Hook de autenticación — gestiona sesión y perfil de usuario
// ============================================================

import { createContext, createElement, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { supabase, obtenerUsuario } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import type { Usuario } from '@/types'
import type { Session } from '@supabase/supabase-js'

interface EstadoAuth {
  usuario: Usuario | null
  sesion: Session | null
  cargando: boolean
  perfilSincronizado: boolean
  error: string | null
}

interface AuthContextValue extends EstadoAuth {
  estaAutenticado: boolean
  actualizarUsuarioLocal: (cambios: Partial<Usuario>) => void
  refrescarUsuario: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Lee la sesión de localStorage sin hacer llamadas de red (instantáneo)
// Supabase v2 guarda la sesión bajo la clave sb-{project-ref}-auth-token
function leerSesionLocal(): Session | null {
  try {
    const projectRef = import.meta.env.VITE_SUPABASE_URL?.split('//')[1]?.split('.')[0] ?? ''
    const key = `sb-${projectRef}-auth-token`
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const data = JSON.parse(raw)
    // Supabase puede guardar { access_token, user, ... } directamente
    if (data?.access_token && data?.user?.id) return data as Session
    // O anidado bajo 'session'
    if (data?.session?.access_token && data?.session?.user?.id) return data.session as Session
    return null
  } catch {
    return null
  }
}

function usuarioFallback(sesion: Session): Usuario {
  return {
    id: sesion.user.id,
    email: sesion.user.email ?? '',
    nombre: sesion.user.user_metadata?.nombre ?? sesion.user.email ?? 'Usuario',
    plan: 'free',
    rol: 'radiologo',
    especialidades: [],
    modalidades: [],
    tema: 'oscuro',
    tamano_fuente: 'md',
    onboarding_completado: true,
    reportes_hoy: 0,
    consultas_hoy: 0,
    ultima_fecha_reset: '',
  }
}

function useAuthState(): AuthContextValue {
  // Inicialización LAZY — solo se ejecuta una vez en el primer render
  const [estado, setEstado] = useState<EstadoAuth>(() => {
    const sesionInicial = leerSesionLocal()
    return {
      usuario: sesionInicial ? usuarioFallback(sesionInicial) : null,
      sesion: sesionInicial,
      cargando: !sesionInicial,
      perfilSincronizado: false,
      error: null,
    }
  })

  const cargarUsuario = useCallback(async (sesion: Session) => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 4000)
      )
      const perfil = await Promise.race([obtenerUsuario(sesion.user.id), timeoutPromise])
      setEstado(prev => ({
        ...prev,
        usuario: perfil ?? usuarioFallback(sesion),
        sesion,
        cargando: false,
        perfilSincronizado: true,
        error: null,
      }))
    } catch {
      setEstado(prev => ({
        ...prev,
        sesion,
        cargando: false,
        usuario: prev.usuario ?? usuarioFallback(sesion),
        perfilSincronizado: true,
      }))
    }
  }, [])

  useEffect(() => {
    let resuelto = false

    // Timeout de seguridad solo si no hay sesión local al montar
    const haySesionLocal = leerSesionLocal() !== null
    const timeout = !haySesionLocal ? setTimeout(() => {
      if (!resuelto) {
        setEstado(prev => ({ ...prev, cargando: false }))
      }
    }, 8000) : null

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, sesion) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && sesion) {
          resuelto = true
          await cargarUsuario(sesion)
        } else if (event === 'SIGNED_OUT') {
          resuelto = true
          setEstado({ usuario: null, sesion: null, cargando: false, perfilSincronizado: false, error: null })
        } else if (event === 'INITIAL_SESSION' && !sesion) {
          resuelto = true
          setEstado({ usuario: null, sesion: null, cargando: false, perfilSincronizado: false, error: null })
        } else if (event === 'TOKEN_REFRESHED' && sesion) {
          setEstado(prev => ({ ...prev, sesion }))
        }
      }
    )

    // Fallback getSession
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (resuelto) return
      resuelto = true
      if (session) {
        cargarUsuario(session)
      } else {
        setEstado(prev => ({ ...prev, cargando: false, perfilSincronizado: false, sesion: null, usuario: null }))
      }
    }).catch(() => {
      if (!resuelto) {
        resuelto = true
        // Mantener sesión local si existe, solo quitar spinner
        setEstado(prev => ({ ...prev, cargando: false }))
      }
    })

    return () => {
      if (timeout) clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [cargarUsuario])

  const actualizarUsuarioLocal = useCallback((cambios: Partial<Usuario>) => {
    setEstado(prev => ({
      ...prev,
      usuario: prev.usuario ? { ...prev.usuario, ...cambios } : null,
    }))
  }, [])

  const refrescarUsuario = useCallback(async () => {
    if (!estado.sesion) return
    await cargarUsuario(estado.sesion)
  }, [estado.sesion, cargarUsuario])

  return {
    usuario: estado.usuario,
    sesion: estado.sesion,
    cargando: estado.cargando,
    perfilSincronizado: estado.perfilSincronizado,
    error: estado.error,
    estaAutenticado: !!estado.sesion,
    actualizarUsuarioLocal,
    refrescarUsuario,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthState()

  useEffect(() => {
    useAppStore.getState().setUsuario(auth.usuario)
  }, [auth.usuario])

  return createElement(AuthContext.Provider, { value: auth }, children)
}

export function useAuth() {
  const auth = useContext(AuthContext)
  if (!auth) {
    throw new Error('useAuth debe usarse dentro de AuthProvider')
  }
  return auth
}
